import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function CreateLeadPage() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    inquiry_type: "", location: "", first_name: "", last_name: "",
    street: "", city: "", state: "", zip_code: "", phone: "", email: "",
    sport_activity: "", num_players: "", venue_option: "", num_guests: "", child_age: "",
    event_type: "", expected_attendance: "", catering_needs: "",
    requested_date: "", start_time: "", duration: "",
    lead_source: "", additional_notes: "",
  });

  useEffect(() => {
    api.get("/venues").then(({ data }) => setVenues(data.venues)).catch(() => {});
  }, []);

  const set = (k, v) => {
    const updated = { ...form, [k]: v };
    if (k === "inquiry_type" && v === "birthday_party") updated.duration = "1.5";
    if (k === "inquiry_type" && v === "picnic") updated.location = "rrdc_field";
    setForm(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.inquiry_type || !form.location || !form.first_name || !form.last_name) {
      toast.error("Fill required fields"); return;
    }
    setLoading(true);
    const payload = {
      ...form,
      num_players: form.num_players ? parseInt(form.num_players) : null,
      num_guests: form.num_guests ? parseInt(form.num_guests) : null,
      child_age: form.child_age ? parseInt(form.child_age) : null,
      expected_attendance: form.expected_attendance ? parseInt(form.expected_attendance) : null,
      duration: form.duration ? parseFloat(form.duration) : null,
    };
    const submit = async (force) => {
      const url = force ? "/leads?force=1" : "/leads";
      const { data } = await api.post(url, payload);
      toast.success("Lead created");
      navigate(`/leads/${data.lead.id}`);
    };
    try {
      await submit(false);
    } catch (err) {
      if (err.response?.status === 409) {
        const overlaps = err.response.data?.detail?.overlaps || [];
        const summary = overlaps.slice(0, 3).map((o) => `• ${o.start_time}–${o.end_time} (${o.state || "block"})`).join("\n");
        const ok = window.confirm(`This time conflicts with existing booking(s):\n\n${summary}${overlaps.length > 3 ? `\n…and ${overlaps.length - 3} more` : ""}\n\nCreate anyway?`);
        if (ok) {
          try { await submit(true); }
          catch (e2) { toast.error(e2.response?.data?.detail || "Failed"); }
        }
      } else {
        toast.error(err.response?.data?.detail || "Failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="create-lead-page">
      <button onClick={() => navigate("/leads")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm" data-testid="back-to-leads-btn">
        <ArrowLeft size={16} /> Back to Leads
      </button>
      <h1 className="font-outfit text-2xl font-bold">New Lead</h1>
      <Card className="border-border">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Inquiry Type *</label>
                <Select value={form.inquiry_type} onValueChange={(v) => set("inquiry_type", v)}>
                  <SelectTrigger data-testid="new-lead-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rental">Rental</SelectItem>
                    <SelectItem value="birthday_party">Birthday Party</SelectItem>
                    <SelectItem value="picnic">School Picnic</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Location *</label>
                <Select value={form.location} onValueChange={(v) => set("location", v)}>
                  <SelectTrigger data-testid="new-lead-location"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rrdc_field">Rolling River Campus</SelectItem>
                    <SelectItem value="turf_house">Turf House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="new-lead-fname" placeholder="First name *" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
              <Input data-testid="new-lead-lname" placeholder="Last name *" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
            </div>
            <Input data-testid="new-lead-email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            <Input data-testid="new-lead-phone" placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            <AddressAutocomplete
              street={form.street}
              city={form.city}
              state={form.state}
              zip={form.zip_code}
              testIdPrefix="new-lead"
              onChange={({ street, city, state, zip }) => setForm(prev => ({ ...prev, street, city, state, zip_code: zip }))}
            />

            {form.inquiry_type === "rental" && (
              <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/30">
                <Input placeholder="Sport/activity" value={form.sport_activity} onChange={(e) => set("sport_activity", e.target.value)} />
                <Input type="number" placeholder="Players" value={form.num_players} onChange={(e) => set("num_players", e.target.value)} />
              </div>
            )}
            {form.inquiry_type === "birthday_party" && (
              <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/30">
                {form.location === "rrdc_field" && venues.length > 0 && (
                  <Select value={form.venue_option} onValueChange={(v) => set("venue_option", v)}>
                    <SelectTrigger><SelectValue placeholder="Venue option" /></SelectTrigger>
                    <SelectContent>
                      {venues.map((v) => <SelectItem key={v.id} value={v.name}>{v.name} - ${v.starting_price}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Input type="number" placeholder="Guests" value={form.num_guests} onChange={(e) => set("num_guests", e.target.value)} />
                <Input type="number" placeholder="Child age" value={form.child_age} onChange={(e) => set("child_age", e.target.value)} />
              </div>
            )}
            {form.inquiry_type === "event" && (
              <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/30">
                <Input placeholder="Event type" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} />
                <Input type="number" placeholder="Attendance" value={form.expected_attendance} onChange={(e) => set("expected_attendance", e.target.value)} />
                <Input placeholder="Catering needs" value={form.catering_needs} onChange={(e) => set("catering_needs", e.target.value)} />
              </div>
            )}

            <div className={`grid ${form.inquiry_type === "birthday_party" ? "grid-cols-2" : "grid-cols-3"} gap-3`}>
              <Input type="date" placeholder="Date" value={form.requested_date} onChange={(e) => set("requested_date", e.target.value)} />
              {form.inquiry_type === "birthday_party" && form.location === "rrdc_field" ? (
                <Select value={form.start_time} onValueChange={(v) => set("start_time", v)}>
                  <SelectTrigger data-testid="new-lead-time-slot"><SelectValue placeholder="Time slot" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="11:00">11:00 AM - 12:30 PM</SelectItem>
                    <SelectItem value="14:00">2:00 PM - 3:30 PM</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input type="time" placeholder="Time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} />
              )}
              {form.inquiry_type !== "birthday_party" && (
                <Input type="number" placeholder="Hours" min="1" step="0.5" value={form.duration} onChange={(e) => set("duration", e.target.value)} />
              )}
              {form.inquiry_type === "birthday_party" && (
                <p className="text-xs text-muted-foreground col-span-2 mt-1">Birthday parties are 90-minute blocks.</p>
              )}
            </div>

            <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
              <SelectTrigger><SelectValue placeholder="Lead source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Google">Google</SelectItem>
                <SelectItem value="Referral">Referral</SelectItem>
                <SelectItem value="Social Media">Social Media</SelectItem>
                <SelectItem value="Walk-in">Walk-in</SelectItem>
                <SelectItem value="Phone">Phone</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>

            <textarea
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[60px]"
              placeholder="Notes"
              value={form.additional_notes}
              onChange={(e) => set("additional_notes", e.target.value)}
            />

            <Button data-testid="create-lead-submit" type="submit" className="w-full" disabled={loading}>
              {loading ? "Creating..." : "Create Lead"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
